import {
  Code2, BrainCircuit, CloudCog, DatabaseZap, Network, Rocket,
  Landmark, HeartPulse, ShoppingBag, ShieldCheck, RadioTower, Plane,
  Factory, Truck, Building2, Cpu, Sparkles, Bot, LineChart, GitBranch,
  Layers, Workflow, Lock, Compass, PenTool, Hammer, Plug, TrendingUp,
} from "lucide-react";

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/solutions", label: "Solutions" },
  { to: "/ai-data", label: "AI & Data" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
];

export const SERVICES = [
  {
    num: "01", slug: "digital-engineering", icon: Code2,
    title: "Digital Engineering",
    tagline: "Build scalable enterprise applications and modern digital experiences.",
    items: ["Web Applications", "Mobile Applications", "Enterprise Platforms", "Product Engineering", "UI/UX Engineering"],
    detail: "From customer-facing portals to complex internal platforms, we engineer digital products that are fast, resilient and designed around measurable business outcomes — from solution architecture to production release.",
  },
  {
    num: "02", slug: "ai-generative-ai", icon: BrainCircuit,
    title: "AI & Generative AI",
    tagline: "Turn AI from experimentation into real business capabilities.",
    items: ["Generative AI", "AI Agents", "RAG Systems", "LLM Integration", "AI Automation", "AI Assistants", "Machine Learning", "Predictive Analytics"],
    detail: "We design and deploy production-grade AI: retrieval-augmented assistants, autonomous agents, ML platforms and LLM-powered workflows that integrate safely with your data, systems and governance model.",
  },
  {
    num: "03", slug: "cloud-architecture", icon: CloudCog,
    title: "Cloud & Architecture",
    tagline: "Design secure, scalable and resilient technology foundations.",
    items: ["Solution Architecture", "Cloud Architecture", "Microservices", "API Architecture", "Distributed Systems", "Cloud Migration", "Application Modernization"],
    detail: "Architecture is where scale is won or lost. We design cloud-native foundations, microservices landscapes and migration paths that keep enterprises secure, observable and ready to grow.",
  },
  {
    num: "04", slug: "data-analytics", icon: DatabaseZap,
    title: "Data & Analytics",
    tagline: "Transform data into intelligent business decisions.",
    items: ["Data Engineering", "Data Pipelines", "Data Warehousing", "Big Data", "BI & Analytics", "Real-time Data", "ML Data Platforms"],
    detail: "We build the pipelines, platforms and real-time data products that turn fragmented data into a trusted, decision-ready asset — and the foundation every serious AI initiative depends on.",
  },
  {
    num: "05", slug: "enterprise-integration", icon: Network,
    title: "Enterprise Integration",
    tagline: "Connect applications, systems and data across the enterprise.",
    items: ["API Integration", "System Integration", "Event-driven Architecture", "Kafka", "Enterprise Middleware", "Legacy Modernization"],
    detail: "Enterprises run on connection. We design event-driven backbones, API ecosystems and integration layers that make legacy systems and modern platforms operate as one coherent whole.",
  },
  {
    num: "06", slug: "devops-platform", icon: Rocket,
    title: "DevOps & Platform Engineering",
    tagline: "Accelerate software delivery with reliable engineering platforms.",
    items: ["CI/CD", "Docker", "Kubernetes", "OpenShift", "Infrastructure Automation", "Monitoring", "Observability"],
    detail: "We build internal engineering platforms, automated delivery pipelines and observability stacks that let teams ship faster with confidence — safely, repeatedly and at enterprise scale.",
  },
];

export const AI_LAYERS = [
  { id: "user", name: "User", desc: "Employees, customers and partners engaging through intelligent, assistive experiences.", stack: ["Conversational UX", "Copilot Interfaces", "Web & Mobile"] },
  { id: "application", name: "Application", desc: "Product surfaces where AI capability is embedded into real workflows.", stack: ["AI-native Apps", "Workflow Embedding", "Assistants"] },
  { id: "api-agent", name: "API / Agent Layer", desc: "Orchestration that routes intent, calls tools and coordinates autonomous agents.", stack: ["Agent Orchestration", "Tool Calling", "Guardrails"] },
  { id: "llm-ml", name: "LLM / ML Models", desc: "Foundation models and custom machine learning, selected and tuned per use case.", stack: ["LLMs", "Fine-tuning", "Classical ML"] },
  { id: "rag-vector", name: "RAG / Vector Database", desc: "Retrieval that grounds model output in your verified enterprise knowledge.", stack: ["Vector Search", "Embeddings", "Hybrid Retrieval"] },
  { id: "enterprise-data", name: "Enterprise Data", desc: "Governed, high-quality data products feeding every intelligent capability.", stack: ["Data Pipelines", "Warehousing", "Governance"] },
  { id: "cloud-infra", name: "Cloud Infrastructure", desc: "Secure, scalable compute and MLOps foundations that keep AI in production.", stack: ["Multi-cloud", "MLOps", "Security & Cost"] },
];

export const AI_FEATURES = [
  { name: "Generative AI", desc: "Content, code and knowledge generation embedded in real workflows.", icon: Sparkles },
  { name: "AI Agents", desc: "Autonomous, tool-using agents that execute multi-step business tasks.", icon: Bot },
  { name: "Machine Learning", desc: "Custom models for classification, forecasting and decision support.", icon: LineChart },
  { name: "RAG", desc: "Retrieval-augmented generation grounded in your enterprise knowledge.", icon: DatabaseZap },
  { name: "LLM Integration", desc: "Safe, observable integration of foundation models into existing systems.", icon: Plug },
  { name: "Predictive Analytics", desc: "Forward-looking intelligence built on your historical data.", icon: TrendingUp },
  { name: "AI Automation", desc: "Intelligent automation of document, service and operations workflows.", icon: Workflow },
  { name: "Responsible AI", desc: "Governance, evaluation and safety controls designed in from day one.", icon: Lock },
];

export const ARCH_LAYERS = [
  { name: "Experience Layer", desc: "Design systems & journeys" },
  { name: "Web / Mobile / Channels", desc: "Every customer touchpoint" },
  { name: "API & Integration", desc: "Gateways & contracts" },
  { name: "Microservices", desc: "Domain-driven services" },
  { name: "Event Streaming", desc: "Real-time backbone" },
  { name: "Data Platform", desc: "Trusted data products" },
  { name: "AI / ML", desc: "Intelligence in production" },
  { name: "Cloud Infrastructure", desc: "Secure & elastic" },
];

export const INDUSTRIES = [
  { id: "banking", name: "Banking & Financial Services", icon: Landmark, help: "Modernize core banking platforms, automate processes, build secure digital channels and enable intelligent customer experiences." },
  { id: "healthcare", name: "Healthcare", icon: HeartPulse, help: "Interoperable data platforms, clinical workflow digitization and AI-assisted diagnostics with privacy by design." },
  { id: "retail", name: "Retail & E-commerce", icon: ShoppingBag, help: "Omnichannel commerce platforms, personalization engines and real-time inventory intelligence." },
  { id: "insurance", name: "Insurance", icon: ShieldCheck, help: "Automated claims processing, underwriting intelligence and digital policy servicing at scale." },
  { id: "telecom", name: "Telecommunications", icon: RadioTower, help: "Network analytics, customer experience platforms and AI-driven service operations." },
  { id: "travel", name: "Travel & Hospitality", icon: Plane, help: "Booking platform modernization, dynamic pricing systems and personalized guest experiences." },
  { id: "logistics", name: "Logistics", icon: Truck, help: "Real-time tracking platforms, route optimization and supply-chain visibility systems." },
  { id: "government", name: "Government & Public Services", icon: Building2, help: "Secure citizen services, legacy modernization and transparent data platforms." },
  { id: "technology", name: "Technology & Software", icon: Cpu, help: "Product engineering acceleration, platform architecture and AI feature development." },
];

export const CASE_STUDIES = [
  {
    index: "01", slug: "digital-banking", title: "Digital Banking Transformation",
    challenge: "Legacy banking workflows and fragmented services slowed every product launch.",
    solution: "Modern microservices architecture, API integration and new digital channels.",
    tech: ["Java", "Spring Boot", "Angular", "Kafka", "Oracle", "OpenShift"],
    outcome: "A scalable, resilient digital banking platform ready for continuous delivery.",
    metrics: [
      { value: 6, suffix: "x", label: "Faster release cycles" },
      { value: 99.99, suffix: "%", label: "Platform availability" },
    ],
  },
  {
    index: "02", slug: "enterprise-ai-assistant", title: "AI-Powered Enterprise Assistant",
    challenge: "Employees struggled to find information across fragmented knowledge sources.",
    solution: "Enterprise RAG + LLM + intelligent search, grounded in verified internal knowledge.",
    tech: ["Python", "FastAPI", "LLM", "Vector DB", "RAG"],
    outcome: "Faster knowledge discovery and measurably improved team productivity.",
    metrics: [
      { value: 70, suffix: "%", label: "Faster answers" },
      { value: 40, suffix: "+", label: "Knowledge sources unified" },
    ],
  },
  {
    index: "03", slug: "cloud-data-platform", title: "Cloud Data Platform",
    challenge: "Disconnected data pipelines and reporting systems delayed every decision.",
    solution: "A modern cloud-native data platform with streaming and unified analytics.",
    tech: ["Python", "Spark", "Kafka", "Cloud", "Data Warehouse"],
    outcome: "Real-time data processing and scalable analytics across the business.",
    metrics: [
      { value: 12, suffix: "x", label: "Faster data freshness" },
      { value: 65, suffix: "%", label: "Lower reporting latency" },
    ],
  },
];

export const TECH_ECOSYSTEM = [
  { category: "Frontend", items: ["Angular", "React", "Next.js", "TypeScript", "Tailwind CSS", "HTML5", "CSS"] },
  { category: "Backend", items: ["Java", "Spring Boot", "Python", "FastAPI", "Node.js", "GraphQL", "gRPC"] },
  { category: "AI", items: ["OpenAI", "Anthropic", "LLMs", "RAG", "LangChain", "Vector Databases", "AI Agents", "Hugging Face"] },
  { category: "Cloud", items: ["AWS", "Azure", "Google Cloud", "Cloudflare"] },
  { category: "Data", items: ["Kafka", "Spark", "Flink", "Airflow", "dbt", "BigQuery", "Snowflake", "Databricks"] },
  { category: "DevOps", items: ["Docker", "Kubernetes", "OpenShift", "Jenkins", "GitHub Actions", "GitLab", "Terraform", "ArgoCD"] },
  { category: "Databases", items: ["Oracle", "PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch"] },
  { category: "Observability", items: ["Prometheus", "Grafana", "ELK", "OpenTelemetry"] },
  { category: "Quality", items: ["Playwright", "Jest", "SonarQube", "Postman"] },
];

export const TRANSFORMATION = [
  { name: "Legacy", desc: "Monoliths, manual processes, fragmented data" },
  { name: "Modernization", desc: "Re-architecture, APIs, incremental migration" },
  { name: "Cloud", desc: "Elastic, secure, observable infrastructure" },
  { name: "Data", desc: "Unified platforms and real-time pipelines" },
  { name: "AI", desc: "Intelligence embedded in every workflow" },
  { name: "Intelligent Enterprise", desc: "Self-optimizing products and operations" },
];

export const WHY_PRINCIPLES = [
  { num: "01", title: "Engineering First", desc: "We are builders. Every strategy we propose is grounded in what can actually be engineered, shipped and operated.", icon: Hammer },
  { num: "02", title: "Business-Aligned Technology", desc: "Architecture decisions start from business outcomes — revenue, cost, speed and risk — never from technology fashion.", icon: Compass },
  { num: "03", title: "AI-Native Thinking", desc: "We design systems assuming intelligence will be embedded everywhere, and build the data and architecture to support it.", icon: BrainCircuit },
  { num: "04", title: "Architecture That Scales", desc: "From ten users to ten million — foundations designed for growth, resilience and change from day one.", icon: Layers },
  { num: "05", title: "Security by Design", desc: "Security, privacy and compliance are engineered into every layer, not audited in afterwards.", icon: Lock },
  { num: "06", title: "Long-Term Technology Partnership", desc: "We stay accountable beyond launch — evolving, optimizing and governing what we build together.", icon: GitBranch },
];

export const PROCESS_STEPS = [
  { num: "01", title: "Discover", desc: "Understand business objectives and technology challenges.", icon: Compass },
  { num: "02", title: "Strategize", desc: "Define the technology roadmap and transformation strategy.", icon: TrendingUp },
  { num: "03", title: "Architect", desc: "Design scalable and secure architecture.", icon: PenTool },
  { num: "04", title: "Build", desc: "Engineer production-ready digital solutions.", icon: Hammer },
  { num: "05", title: "Integrate", desc: "Connect systems, data, APIs and enterprise platforms.", icon: Plug },
  { num: "06", title: "Scale", desc: "Optimize, automate and continuously evolve.", icon: Rocket },
];

export const INSIGHTS = [
  {
    slug: "ai-agents-enterprise", category: "AI", readTime: "6 min",
    title: "How AI Agents Are Changing Enterprise Applications",
    excerpt: "Agents move AI from answering questions to completing work. Here is what changes in your architecture when software starts acting on intent.",
    body: [
      "Enterprise software has always waited for instructions. AI agents invert that model: given a goal, they plan, call tools, and execute multi-step work across your systems. The shift sounds subtle, but it rewires how applications are designed.",
      "The practical implication is architectural. Agentic systems need well-defined tool contracts, strong identity and permission boundaries, and observability that records not just what happened, but why the agent decided to do it. Teams that treat agents as a UI feature discover this the hard way.",
      "Our recommendation: start with one bounded workflow — a process with clear inputs, verifiable outputs and a human approval step. Prove the operating model there, then scale. Agents reward discipline more than ambition.",
    ],
  },
  {
    slug: "scalable-microservices", category: "Architecture", readTime: "8 min",
    title: "Designing Scalable Microservices Architecture",
    excerpt: "Microservices fail more often from organizational and data boundaries than from technology. A field guide to getting the boundaries right.",
    body: [
      "The hardest part of microservices is not Kubernetes or service meshes — it is deciding where one service ends and another begins. Boundaries drawn along technical layers create distributed monoliths; boundaries drawn along business domains create systems that can evolve independently.",
      "Data ownership is the second pillar. Each service must own its data and expose it through contracts, not shared tables. Event-driven integration, with a streaming backbone such as Kafka, lets services stay decoupled while the business stays consistent.",
      "Finally, invest in the platform before the services multiply: CI/CD, observability, and deployment guardrails. Microservices multiply operational surface area — a strong platform is what keeps that multiplication an advantage instead of a tax.",
    ],
  },
  {
    slug: "rag-to-production", category: "AI", readTime: "7 min",
    title: "From RAG Experiments to Production AI",
    excerpt: "Most RAG demos impress and most RAG deployments disappoint. The gap is evaluation, retrieval quality and governance — not the model.",
    body: [
      "A retrieval-augmented generation demo can be built in an afternoon. Production RAG is a different discipline: chunking strategy, embedding choice, hybrid retrieval, reranking and citation all determine whether answers are trustworthy or confidently wrong.",
      "The single highest-leverage investment is evaluation. Build a golden set of real questions with verified answers before tuning anything. Without it, every change is a guess, and regressions ship silently.",
      "Governance closes the loop: source-level permissions must flow into retrieval, so users only ever see answers grounded in documents they are allowed to read. Get retrieval, evaluation and governance right, and the model becomes the easy part.",
    ],
  },
  {
    slug: "modernizing-legacy", category: "Digital Transformation", readTime: "6 min",
    title: "Modernizing Legacy Enterprise Applications",
    excerpt: "Big-bang rewrites fail. Incremental modernization — strangler patterns, API façades and event bridges — is how legacy estates actually evolve.",
    body: [
      "Every enterprise has a system everyone wants to replace and no one dares to touch. The instinct to rewrite it from scratch is understandable — and almost always wrong. Rewrites stall, scope explodes, and the legacy system keeps running the business in the meantime.",
      "Incremental modernization works because it de-risks each step. Wrap legacy capabilities behind clean API façades, peel off one domain at a time using the strangler pattern, and use event streaming to keep old and new in sync during the transition.",
      "Measure progress in business capabilities migrated, not lines of code retired. Modernization is a journey with a compounding payoff: each increment makes the next one cheaper.",
    ],
  },
  {
    slug: "real-time-data-platforms", category: "Data", readTime: "7 min",
    title: "Building Real-Time Data Platforms",
    excerpt: "Batch reports describe the past. Streaming platforms let the business react to the present. What it takes to make the shift.",
    body: [
      "The economics of data have inverted: storage is cheap, but delayed decisions are expensive. Real-time platforms close that gap by treating data as a continuous stream rather than a nightly batch.",
      "A pragmatic architecture starts with a streaming backbone (Kafka), stream processing where it adds value (Spark or Flink), and a serving layer matched to query patterns. Not everything needs to be real-time — reserve streaming for decisions where minutes matter.",
      "The organizational shift is just as important: data products with clear owners, contracts and SLAs. Real-time data without ownership is just faster chaos.",
    ],
  },
  {
    slug: "cloud-architecture-patterns", category: "Cloud", readTime: "8 min",
    title: "Cloud Architecture Patterns for Modern Enterprises",
    excerpt: "Landing zones, cell-based architecture and platform engineering — the patterns that separate cloud adoption from cloud advantage.",
    body: [
      "Moving to the cloud is a procurement decision; benefiting from it is an architecture decision. Enterprises that lift-and-shift without re-architecting inherit cloud bills without cloud agility.",
      "Three patterns consistently separate the leaders. Landing zones standardize security, networking and identity so teams start compliant by default. Cell-based architectures contain blast radius and scale horizontally. Platform engineering turns infrastructure into a self-service product for developers.",
      "Choose patterns deliberately, document them as paved roads, and let teams move fast inside them. Governance that accelerates is the only governance that survives.",
    ],
  },
];

export const CONTACT_SERVICES = [
  "Web Development", "Mobile Development", "AI / Generative AI", "AI/ML",
  "Cloud Architecture", "Data Engineering", "Enterprise Software",
  "Digital Transformation", "Other",
];

export const PROJECT_TYPES = [
  "New Product Build", "Modernization", "AI Proof of Concept",
  "Architecture Advisory", "Team Augmentation", "Other",
];
